import { useNotification } from '@wheely/ui-kit';
import { useCallback, useMemo, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { usePermissionsContext } from '@/app/contexts/permissions';
import { IntlContext } from '@/i18n';
import { prepareUploadMyDriverForm } from '@/modules/driver/actions/async/prepare-upload-driver-form';
import { DriverDocType } from '@/modules/driver/model/driver-doc-type';
import { getMyDriver } from '@/modules/driver/store/selectors';
import { myDriverUploadSelectors } from '@/modules/driver/store/upload-slice';
import { prepareUploadPartnerForm } from '@/modules/partner/actions/async/preparePartnerUploadForm';
import { reloadPersonData } from '@/modules/partner/actions/async/reload-person-data';
import { partnerAPI } from '@/modules/partner/api';
import { PartnerDocType } from '@/modules/partner/model/partner-doc-type';
import { getPartner } from '@/modules/partner/store/selectors';
import { partnerUploadSelectors } from '@/modules/partner/store/uploadSlice';
import { AppDispatch } from '@/root-store';
import { errorHandling } from '@/services/errorHandling';
import { downloadBlob } from '@/shared/utils/downloadBlob';
import { formatDate } from '@/shared/utils/formatDate';
import { UploadMyDriver, UploadPartner } from '@/shared/view/Upload';
import { useCollapsedDocsList } from '@/shared/view/Upload/SupplementaryDocs/useCollapsedDocsList';
import { IDocsListItem } from '@src-common/view/components/DocsList';

import { ProfileView } from './view';

interface IProps {
  onNavigate: (uri: string) => void;
  onNavigateBack: (uri: string) => void;
}

const isDriverDoc = Symbol('isDriverDoc');

interface IDoc extends IDocsListItem {
  [isDriverDoc]: boolean;
}

const mergeDocListItems = (forPartner: IDocsListItem[], forDriver: IDocsListItem[]): IDoc[] => [
  ...forDriver.map(document => ({ ...document, [isDriverDoc]: true })),
  ...forPartner.map(document => ({ ...document, [isDriverDoc]: false })),
];

const Profile = ({ onNavigate, onNavigateBack }: IProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = usePermissionsContext();
  const { region } = useContext(IntlContext);

  const intl = useIntl();
  const { showNotification } = useNotification();
  const partner = useSelector(getPartner);

  const [isDriverUploading, setIsDriverUploading] = useState(false);
  const [isLegalUploading, setIsLegalUploading] = useState(false);
  const [isAgreementLoading, setIsAgreementLoading] = useState(false);

  const handleClickEditPayment = useCallback(() => {
    onNavigate('/profile/payments');
  }, [onNavigate]);

  const handleClickEditPayouts = useCallback(
    ({ isCompany }: { isCompany?: boolean } = {}) => {
      onNavigate(isCompany ? '/profile/payouts-company' : '/profile/payouts');
    },
    [onNavigate],
  );

  const handleClickEditDriver = useCallback(() => {
    onNavigate('/profile/chauffeur');
  }, [onNavigate]);

  const handleClickContactInformation = useCallback(() => {
    onNavigate('/profile/contact-information');
  }, [onNavigate]);

  const handleClickBack = useCallback(() => {
    onNavigateBack('/todo');
  }, [onNavigateBack]);

  const handleClickAgencyAgreementLink = useCallback(async () => {
    setIsAgreementLoading(true);

    try {
      const pdf = await partnerAPI.getAgencyAgreementConfirmation();

      // Content-Disposition is not exposed across origins, so the name is ours.
      downloadBlob(pdf, `${formatDate(new Date())}_agent_agreement_confirmation_${partner.id}.pdf`);
    } catch (error) {
      errorHandling.captureException(error);

      showNotification({
        message: intl.formatMessage({
          defaultMessage: 'Could not download the document. Please try again later.',
          id: 'profile_agency_agreement_download_failed',
          description: 'Agency agreement download error',
        }),
        showCloseButton: false,
        dark: true,
      });
    } finally {
      setIsAgreementLoading(false);
    }
  }, [intl, partner.id, showNotification]);

  const handleFinishUpload = useCallback(() => {
    dispatch(reloadPersonData());
    setIsDriverUploading(false);
    setIsLegalUploading(false);
  }, [dispatch]);

  const handleSkipUpload = useCallback(() => {
    setIsDriverUploading(false);
    setIsLegalUploading(false);
  }, []);

  const driverDocs = useCollapsedDocsList(
    useSelector(myDriverUploadSelectors.getDocsList),
    'myDriver',
  );
  const partnerDocs = useSelector(partnerUploadSelectors.getDocsList);
  const mergedDocs = useMemo(
    () =>
      user.isFleet
        ? mergeDocListItems(partnerDocs, [])
        : mergeDocListItems(partnerDocs, driverDocs),
    [driverDocs, partnerDocs, user.isFleet],
  );

  const handleUploadDoc = useCallback(
    (_: any, doc: IDoc) => {
      if (doc[isDriverDoc]) {
        dispatch(
          prepareUploadMyDriverForm({
            docType: doc.docType as DriverDocType,
            onSuccess: () => setIsDriverUploading(true),
          }),
        );
      } else {
        dispatch(
          prepareUploadPartnerForm({
            docType: doc.docType as PartnerDocType,
            onSuccess: () => setIsLegalUploading(true),
          }),
        );
      }
    },
    [dispatch],
  );

  const driver = useSelector(getMyDriver);

  return (
    <>
      <ProfileView<IDoc>
        user={user}
        region={region}
        driver={driver}
        docs={mergedDocs}
        onUploadDoc={handleUploadDoc}
        partner={partner}
        onClickEditPayment={handleClickEditPayment}
        onClickEditPayouts={handleClickEditPayouts}
        onClickEditDriver={handleClickEditDriver}
        onClickEditContactInformation={handleClickContactInformation}
        onClickBack={handleClickBack}
        onClickAgencyAgreementLink={handleClickAgencyAgreementLink}
        isAgencyAgreementLoading={isAgreementLoading}
      />
      {isDriverUploading && (
        <UploadMyDriver onClickSkip={handleSkipUpload} onFinishUpload={handleFinishUpload} />
      )}
      {isLegalUploading && (
        <UploadPartner onClickSkip={handleSkipUpload} onFinishUpload={handleFinishUpload} />
      )}
    </>
  );
};

export { Profile };
